import { Controller } from "@hotwired/stimulus";

// Drops a spider from the top of the screen now and then, at most once a day
// per browser, so ops reloading the dashboard all day isn't pestered by it.
// It hangs there until someone hovers or taps it, then scurries back up.
export default class extends Controller {
  static targets = ["swing", "thread", "legs"];

  static CHANCE = 0.25;
  static SEEN_KEY = "danglingSpiderSeenOn";

  connect() {
    if (this.#prefersReducedMotion || this.#seenToday || Math.random() >= this.constructor.CHANCE) {
      this.element.remove();
      return;
    }

    this.#markSeen();
    this.#placeRandomly();
    this.drop();
  }

  drop() {
    this.dropAnimation = this.element.animate(
      [
        { transform: "translateY(-100%)", easing: "cubic-bezier(0.3, 0, 0.6, 1)" },
        { transform: "translateY(3%)", offset: 0.64, easing: "ease-out" },
        { transform: "translateY(-1.5%)", offset: 0.84, easing: "ease-in-out" },
        { transform: "translateY(0)" },
      ],
      { duration: 1600, fill: "forwards" }
    );

    this.swingAnimation = this.swingTarget.animate(
      [
        { transform: "rotate(0deg)" },
        { transform: "rotate(0deg)", offset: 0.28 },
        { transform: "rotate(3deg)", offset: 0.44 },
        { transform: "rotate(-2.5deg)", offset: 0.61 },
        { transform: "rotate(1.5deg)", offset: 0.77 },
        { transform: "rotate(-0.5deg)", offset: 0.91 },
        { transform: "rotate(0deg)" },
      ],
      { duration: 4700, easing: "ease-in-out" }
    );

    // Wiggle, then hold still for a beat: a constant wiggle reads as mechanical.
    this.legAnimations = this.#animateLegs(
      (side) => [
        { transform: "rotate(0deg)" },
        { transform: `rotate(${-5 * side}deg)`, offset: 0.1 },
        { transform: `rotate(${4 * side}deg)`, offset: 0.2 },
        { transform: `rotate(${-4 * side}deg)`, offset: 0.3 },
        { transform: "rotate(0deg)", offset: 0.4 },
        { transform: "rotate(0deg)" },
      ],
      { duration: 2400, iterations: Infinity, easing: "ease-in-out" }
    );
  }

  scare() {
    if (this.scared) return;
    this.scared = true;

    const position = getComputedStyle(this.element).transform;
    const tilt = getComputedStyle(this.swingTarget).transform;
    this.dropAnimation.cancel();
    this.swingAnimation.cancel();
    this.legAnimations.forEach((animation) => animation.cancel());

    this.#animateLegs(
      (side) => [
        { transform: "rotate(0deg)" },
        { transform: `rotate(${-9 * side}deg)` },
        { transform: "rotate(0deg)" },
        { transform: `rotate(${7 * side}deg)` },
        { transform: "rotate(0deg)" },
      ],
      { duration: 220, iterations: Infinity }
    );
    this.swingTarget.animate([{ transform: tilt }, { transform: "rotate(0deg)" }], {
      duration: 300,
      fill: "forwards",
    });

    const scurry = this.element.animate(
      [{ transform: position }, { transform: "translateY(-100%)" }],
      { duration: 550, easing: "cubic-bezier(0.4, 0, 0.9, 0.6)", fill: "forwards" }
    );
    scurry.finished.then(() => this.element.remove()).catch(() => {});
  }

  // `side` flips the rotation for the right legs, so the two sides mirror.
  #animateLegs(keyframesFor, options) {
    return this.legsTargets.map((legs, index) =>
      legs.animate(keyframesFor(index === 0 ? 1 : -1), options)
    );
  }

  #placeRandomly() {
    this.element.style.right = `${20 + Math.random() * 45}%`;
    this.threadTarget.style.height = `${18 + Math.random() * 22}vh`;
  }

  get #prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  get #today() {
    return new Date().toDateString();
  }

  get #seenToday() {
    try {
      return localStorage.getItem(this.constructor.SEEN_KEY) === this.#today;
    } catch {
      return false;
    }
  }

  #markSeen() {
    try {
      localStorage.setItem(this.constructor.SEEN_KEY, this.#today);
    } catch {}
  }
}
